import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiV1 } from '@/api/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AcceptInvite — an invited external partner (agency) chooses their password
 * from the single-use link in their invite email, then signs in with email
 * and password. Same green shell as Login. See backend/auth/partners.py.
 */
export default function AcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [invite, setInvite] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setError('This link is missing its code. Open the link from your invite email.'); return; }
    apiV1.get('/auth/partner-invite', { params: { token } })
      .then((r) => setInvite(r.data?.data || {}))
      .catch((e) => setError(e?.message || 'This link has expired or was already used.'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error('The passwords don\'t match'); return; }
    setSaving(true);
    try {
      await apiV1.post('/auth/partner-invite/accept', { token, password: form.password });
      toast.success('Password set. Sign in with your email and new password.');
      navigate(`/login?partner=1&email=${encodeURIComponent(invite?.email || '')}`, { replace: true });
    } catch (err) {
      toast.error(err?.message || 'Could not set your password');
    } finally {
      setSaving(false);
    }
  };

  const label = { display: 'block', textAlign: 'left', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.82)', margin: '14px 0 4px' };
  const darkInput = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 14, fontFamily: 'inherit' };
  const hint = { fontSize: 11, color: 'rgba(255,255,255,.6)', margin: '4px 0 0', textAlign: 'left' };

  return (
    <div className="pgp-auth-shell pgp-scope" data-testid="accept-invite-page">
      <div className="pgp-auth-shell__inner">
        <img className="pgp-auth-shell__logo" src="/pgp-gates-white.svg" alt="Pam Golding Properties" />
        <p className="pgp-eyebrow">Pam Golding Properties</p>
        <h1 className="pgp-wordmark">LinkHub</h1>
        <div className="pgp-auth-shell__rule" />

        {error ? (
          <p className="pgp-auth-shell__lede" data-testid="accept-invite-error">{error}</p>
        ) : invite === null ? (
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#fff', margin: '0 auto' }} />
        ) : (
          <>
            <p className="pgp-auth-shell__lede">
              Welcome{invite.full_name ? `, ${invite.full_name}` : ''}. Choose a password for{' '}
              <strong>{invite.email}</strong>.
            </p>
            <form onSubmit={handleSubmit}>
              <label style={label} htmlFor="password">New password</label>
              <input id="password" type="password" style={darkInput} autoComplete="new-password"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="accept-password-input" />
              <p style={hint}>At least 8 characters, with upper and lower case, a number and a symbol.</p>
              <label style={label} htmlFor="confirm">Confirm password</label>
              <input id="confirm" type="password" style={darkInput} autoComplete="new-password"
                value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="accept-confirm-input" />
              <button type="submit" className="pgp-btn pgp-btn--on-dark" style={{ marginTop: 18 }}
                disabled={saving || !form.password || !form.confirm} data-testid="accept-submit-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Set password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
